import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreateFileUploadUrlOptions,
  CreateFileUploadUrlResponse,
  DataMovementJob,
  DataMovementJobQuery,
  Destination,
  JobCreatedEnvelope,
  JobDeletedEnvelope,
  JobUpdatedEnvelope,
  JobsQueryBuilder,
  ListJobsOptions,
  ListJobsResponse,
  ListMovementLogsOptions,
  ListMovementLogsResponse,
  QueryJobsResponse,
  Source,
  SubmitJobOptions,
  SubmitJobResponse,
  TerminateJobResponse,
  createFileUploadUrl as universalCreateFileUploadUrl,
  getJob as universalGetJob,
  listJobs as universalListJobs,
  listMovementLogs as universalListMovementLogs,
  queryJobs as universalQueryJobs,
  submitJob as universalSubmitJob,
  terminateJob as universalTerminateJob,
  typedQueryJobs as universalTypedQueryJobs,
} from './hub-v1-job-movement-jobs.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/data' };

export function submitJob(httpClient: HttpClient): SubmitJobSignature {
  return (
    source: NonNullablePaths<
      Source,
      | `file.fileId`
      | `localization.languages`
      | `wixDataCollection.collectionId`,
      3
    >,
    destination: NonNullablePaths<
      Destination,
      | `file.format`
      | `localization.languages`
      | `wixDataCollection.collectionId`,
      3
    >,
    options?: SubmitJobOptions
  ) =>
    universalSubmitJob(
      source,
      destination,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SubmitJobSignature {
  /**
   * Submit a data movement job.
   *
   * > **Note**: By default, the job is submitted for the current site.
   * @param - Source to move data from.
   * @param - Destination to move data to.
   */
  (
    source: NonNullablePaths<
      Source,
      | `file.fileId`
      | `localization.languages`
      | `wixDataCollection.collectionId`,
      3
    >,
    destination: NonNullablePaths<
      Destination,
      | `file.format`
      | `localization.languages`
      | `wixDataCollection.collectionId`,
      3
    >,
    options?: SubmitJobOptions
  ): Promise<
    NonNullablePaths<
      SubmitJobResponse,
      | `job.partiallySuccessfulOptions.cause`
      | `job._id`
      | `job.status`
      | `job.source.wixDataCollection.collectionId`
      | `job.source.wixDataCollection.fields`
      | `job.source.localization.languages`
      | `job.source.file.format`
      | `job.source.file.fileId`
      | `job.source.file.url`
      | `job.destination.wixDataCollection.collectionId`
      | `job.destination.wixDataCollection.writePolicy`
      | `job.destination.localization.languages`
      | `job.destination.file.format`
      | `job.destination.file.url`
      | `job.name`
      | `job.progress.current`
      | `job.progress.itemsRejected`
      | `job.progress.itemsSuccessful`
      | `job.error.code`
      | `job.error.description`
      | `job.sourceSiteId`
      | `job.destinationSiteId`
      | `job.logsRecorded`,
      5
    >
  >;
}

export function createFileUploadUrl(
  httpClient: HttpClient
): CreateFileUploadUrlSignature {
  return (options?: CreateFileUploadUrlOptions) =>
    universalCreateFileUploadUrl(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateFileUploadUrlSignature {
  /**
   * Creates an upload URL for a data source file.
   *
   * Before submitting a job to import data from a file to a collection, call this method to generate an upload URL. Use the `uploadUrl` in the response to upload your source file. For example:
   *
   * `curl --request PUT --upload-file "${path_to_file}" "${uploadUrl}"`
   *
   * > **Note**: The URL is valid for 15 minutes.
   */
  (options?: CreateFileUploadUrlOptions): Promise<
    NonNullablePaths<CreateFileUploadUrlResponse, `fileId` | `uploadUrl`, 2>
  >;
}

export function getJob(httpClient: HttpClient): GetJobSignature {
  return (jobId: string) =>
    universalGetJob(
      jobId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetJobSignature {
  /**
   * Retrieves the specified data movement job.
   *
   * > **Note**: You can only retrieve jobs submitted for the current site.
   * @param - Job ID.
   * @returns Details of the retrieved job.
   */
  (jobId: string): Promise<
    NonNullablePaths<
      DataMovementJob,
      | `partiallySuccessfulOptions.cause`
      | `_id`
      | `status`
      | `source.wixDataCollection.collectionId`
      | `source.wixDataCollection.fields`
      | `source.localization.languages`
      | `source.file.format`
      | `source.file.fileId`
      | `source.file.url`
      | `destination.wixDataCollection.collectionId`
      | `destination.wixDataCollection.writePolicy`
      | `destination.localization.languages`
      | `destination.file.format`
      | `destination.file.url`
      | `name`
      | `progress.current`
      | `progress.itemsRejected`
      | `progress.itemsSuccessful`
      | `error.code`
      | `error.description`
      | `sourceSiteId`
      | `destinationSiteId`
      | `logsRecorded`,
      4
    >
  >;
}

export function listJobs(httpClient: HttpClient): ListJobsSignature {
  return (options?: ListJobsOptions) =>
    universalListJobs(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListJobsSignature {
  /**
   * Lists data movement jobs for the current site.
   *
   * The `listJobs()` method builds a query to retrieve a list of jobs for the current site, and returns a [`JobsQueryBuilder`](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/jobs-query-builder/ascending) object. The returned object contains the query definition, which is typically used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/jobs-query-builder/find) method.
   *
   * You can refine the query by chaining `JobsQueryBuilder` methods onto the query. `JobsQueryBuilder` methods enable you to sort, filter, and control the results `listJobs()` returns.
   *
   * `listJobs()` runs with these `JobsQueryBuilder` defaults, which you can override:
   *
   * - [`skipTo`: `0`](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/jobs-query-builder/skip-to)
   * - [`limit`: `50`](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/jobs-query-builder/limit)
   * - [`ascending`: by `_id`](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/jobs-query-builder/ascending)
   *
   * The methods chained to `listJobs()` are applied in the order they're called. For example, if you apply `ascending('firstName')` and then `descending('age')`, the results are sorted first by firstName, and then, if there are multiple results with the same `firstName`, the items are sorted by `age`.
   */
  (options?: ListJobsOptions): Promise<
    NonNullablePaths<
      ListJobsResponse,
      | `jobs`
      | `jobs.${number}.partiallySuccessfulOptions.cause`
      | `jobs.${number}._id`
      | `jobs.${number}.status`
      | `jobs.${number}.source.wixDataCollection.collectionId`
      | `jobs.${number}.source.file.format`
      | `jobs.${number}.source.file.fileId`
      | `jobs.${number}.source.file.url`
      | `jobs.${number}.destination.wixDataCollection.collectionId`
      | `jobs.${number}.destination.wixDataCollection.writePolicy`
      | `jobs.${number}.destination.file.format`
      | `jobs.${number}.destination.file.url`
      | `jobs.${number}.name`
      | `jobs.${number}.progress.current`
      | `jobs.${number}.progress.itemsRejected`
      | `jobs.${number}.progress.itemsSuccessful`
      | `jobs.${number}.error.code`
      | `jobs.${number}.error.description`
      | `jobs.${number}.sourceSiteId`
      | `jobs.${number}.destinationSiteId`
      | `jobs.${number}.logsRecorded`,
      6
    >
  >;
}

export function queryJobs(httpClient: HttpClient): QueryJobsSignature {
  return () =>
    universalQueryJobs(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryJobsSignature {
  /**
   * Retrieves data movement jobs for the current site, on the basis of the filtering, sorting, and paging preferences you provide.
   *
   * Learn more about [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language) and [sorting and paging](https://dev.wix.com/docs/rest/articles/get-started/sorting-and-paging) configuration.
   */
  (): JobsQueryBuilder;
}

export function typedQueryJobs(
  httpClient: HttpClient
): TypedQueryJobsSignature {
  return (query: DataMovementJobQuery) =>
    universalTypedQueryJobs(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryJobsSignature {
  /** */
  (query: DataMovementJobQuery): Promise<
    NonNullablePaths<
      QueryJobsResponse,
      | `jobs`
      | `jobs.${number}.partiallySuccessfulOptions.cause`
      | `jobs.${number}._id`
      | `jobs.${number}.status`
      | `jobs.${number}.source.wixDataCollection.collectionId`
      | `jobs.${number}.source.file.format`
      | `jobs.${number}.source.file.fileId`
      | `jobs.${number}.source.file.url`
      | `jobs.${number}.destination.wixDataCollection.collectionId`
      | `jobs.${number}.destination.wixDataCollection.writePolicy`
      | `jobs.${number}.destination.file.format`
      | `jobs.${number}.destination.file.url`
      | `jobs.${number}.name`
      | `jobs.${number}.progress.current`
      | `jobs.${number}.progress.itemsRejected`
      | `jobs.${number}.progress.itemsSuccessful`
      | `jobs.${number}.error.code`
      | `jobs.${number}.error.description`
      | `jobs.${number}.sourceSiteId`
      | `jobs.${number}.destinationSiteId`
      | `jobs.${number}.logsRecorded`,
      6
    >
  >;
}

export function terminateJob(httpClient: HttpClient): TerminateJobSignature {
  return (jobId: string) =>
    universalTerminateJob(
      jobId,
      // @ts-ignore
      { httpClient }
    );
}

interface TerminateJobSignature {
  /**
   * Terminates the specified job.
   * @param - ID of the job to terminate.
   */
  (jobId: string): Promise<
    NonNullablePaths<
      TerminateJobResponse,
      | `job.partiallySuccessfulOptions.cause`
      | `job._id`
      | `job.status`
      | `job.source.wixDataCollection.collectionId`
      | `job.source.wixDataCollection.fields`
      | `job.source.localization.languages`
      | `job.source.file.format`
      | `job.source.file.fileId`
      | `job.source.file.url`
      | `job.destination.wixDataCollection.collectionId`
      | `job.destination.wixDataCollection.writePolicy`
      | `job.destination.localization.languages`
      | `job.destination.file.format`
      | `job.destination.file.url`
      | `job.name`
      | `job.progress.current`
      | `job.progress.itemsRejected`
      | `job.progress.itemsSuccessful`
      | `job.error.code`
      | `job.error.description`
      | `job.sourceSiteId`
      | `job.destinationSiteId`
      | `job.logsRecorded`,
      5
    >
  >;
}

export function listMovementLogs(
  httpClient: HttpClient
): ListMovementLogsSignature {
  return (jobId: string, options?: ListMovementLogsOptions) =>
    universalListMovementLogs(
      jobId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListMovementLogsSignature {
  /**
   * Lists logs for the specified job.
   *
   * Logs are generated by the destination for each item that failed to move. Some destinations also generate logs for items that moved successfully.
   * @param - ID of the job for which to list logs.
   */
  (jobId: string, options?: ListMovementLogsOptions): Promise<
    NonNullablePaths<
      ListMovementLogsResponse,
      | `logs`
      | `logs.${number}.sourceItemId`
      | `logs.${number}.failure.code`
      | `logs.${number}.failure.description`,
      5
    >
  >;
}

export const onJobCreated = EventDefinition(
  'wix.hub.v1.job_created',
  true,
  (event: JobCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.startedAt' },
            { path: 'entity.finishedAt' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<JobCreatedEnvelope>();
export const onJobDeleted = EventDefinition(
  'wix.hub.v1.job_deleted',
  true,
  (event: JobDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.startedAt' },
            { path: 'undefined.finishedAt' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<JobDeletedEnvelope>();
export const onJobUpdated = EventDefinition(
  'wix.hub.v1.job_updated',
  true,
  (event: JobUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.startedAt' },
            { path: 'entity.finishedAt' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<JobUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ApplicationError,
  BaseEventMetadata,
  CreateFileUploadUrlOptions,
  CreateFileUploadUrlRequest,
  CreateFileUploadUrlResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DataMovementJob,
  DataMovementJobLogs,
  DataMovementJobQuerySpec,
  DataMovementJobStatusOptionsOneOf,
  Destination,
  DestinationDestinationOneOf,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  ExternalFileSource,
  FailedOptions,
  Field,
  FieldType,
  FileDestination,
  FileFormat,
  FileSource,
  GetJobRequest,
  GetJobResponse,
  GoogleSheetsSource,
  IdentificationData,
  IdentificationDataIdOneOf,
  InProgressOptions,
  ItemsSuccessfulInfo,
  JobCallback,
  JobCallbackOptionsOneOf,
  JobCreatedEnvelope,
  JobDeletedEnvelope,
  JobStatus,
  JobUpdatedEnvelope,
  JobsQueryBuilder,
  JobsQueryResult,
  ListJobsOptions,
  ListJobsRequest,
  ListJobsResponse,
  ListMovementLogsOptions,
  ListMovementLogsRequest,
  ListMovementLogsResponse,
  LocalizationDestination,
  LocalizationSource,
  MediaType,
  MessageEnvelope,
  MovementLog,
  Paging,
  PartiallySuccessfulOptions,
  PartiallySuccessfulStatusOptions,
  Progress,
  QueryJobsRequest,
  QueryJobsResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RestoreInfo,
  Schema,
  SortOrder,
  Sorting,
  Source,
  SourceSourceOneOf,
  StoresCatalogInventoryDestination,
  StoresCatalogProductsDestination,
  SubmitJobOptions,
  SubmitJobOptionsSiteOverrideOneOf,
  SubmitJobRequest,
  SubmitJobRequestSiteOverrideOneOf,
  SubmitJobResponse,
  TerminateJobByIdRequest,
  TerminateJobByIdResponse,
  TerminateJobRequest,
  TerminateJobResponse,
  TestDestination,
  TestSource,
  TestTransformation,
  Transformation,
  TransformationTransformationOneOf,
  Units,
  WebhookIdentityType,
  WixDataCollectionDefinitionDestination,
  WixDataCollectionDefinitionSource,
  WixDataDestination,
  WixDataDestinationWritePolicy,
  WixDataEnvironment,
  WixDataSource,
  WixMediaDestination,
  WixMediaDestinationWritePolicy,
  WixMediaSource,
  WritePolicy,
  utils,
} from './hub-v1-job-movement-jobs.universal.js';
